import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, Copy, ShieldCheck } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Aurora } from '../components/feedback/Beacon';
import { Tide } from '../components/feedback/Progress';
import { toast } from '../components/feedback/toast';
import { LanguageToggle } from '../components/shell/LanguageToggle';
import { Button } from '../components/ui/Button';
import { OtpInput } from '../components/ui/OtpInput';
import { api, type MfaStatus } from '../lib/api';
import { useTheme } from '../theme/ThemeProvider';
import { THEMES } from '../theme/themes';
import { ThemeSwitcher } from '../theme/ThemeSwitcher';
import { authErrorKey, beginTotpSetup, finishTotpSetup } from './cognito';
import { useMe } from './session';

// Authenticator (TOTP) enrollment (docs/architecture.md §9a). Shown when the API says MFA is
// required and blocking, or from the security checklist. After Cognito confirms the code, the
// API re-checks with Cognito (/v1/me/mfa/sync) and stops blocking.

export function MfaSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const { resolved } = useTheme();
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<'preparing' | 'ready' | 'verifying' | 'done'>('preparing');
  const [copied, setCopied] = useState(false);

  const email = me.data?.user.email ?? '';
  const colors = THEMES[resolved];

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    void beginTotpSetup(email)
      .then(async ({ secret, uri }) => {
        const qr = await QRCode.toDataURL(uri, {
          margin: 1,
          width: 220,
          color: { dark: colors.scheme === 'dark' ? '#0B0F1A' : colors.primary, light: '#FFFFFF' },
          errorCorrectionLevel: 'M',
        });
        if (!cancelled) {
          setSetup({ secret, qr });
          setState('ready');
        }
      })
      .catch((err: unknown) => setError(t(authErrorKey(err))));
    return () => {
      cancelled = true;
    };
    // Re-colour the QR code only on first load; theme changes keep it readable (white tile).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const verify = async (value: string) => {
    setState('verifying');
    setError(null);
    try {
      await finishTotpSetup(value);
      const { mfa } = await api<{ mfa: MfaStatus }>('/v1/me/mfa/sync', { method: 'POST' });
      if (!mfa.enrolled) throw new Error('not enrolled');
      setState('done');
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success(t('mfa.setup.success'));
      setTimeout(() => void navigate({ to: '/' }), 1400);
    } catch (err) {
      setState('ready');
      setCode('');
      setError(t(authErrorKey(err)));
    }
  };

  const reason =
    me.data?.mfa.reason === 'platform'
      ? t('mfa.setup.reasonPlatform')
      : t('mfa.setup.reasonOrganization');

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden bg-bg px-5 py-16">
      <Aurora intensity="soft" />
      <div className="absolute right-4 top-4 z-10 flex gap-2 sm:right-6 sm:top-6">
        <LanguageToggle id="mfa-lang" />
        <ThemeSwitcher />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass relative w-full max-w-[560px] rounded-xl p-7 elevated sm:p-10"
      >
        <AnimatePresence mode="wait">
          {state === 'done' ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4 py-8 text-center"
            >
              <svg viewBox="0 0 52 52" className="h-20 w-20" aria-hidden="true">
                <motion.circle
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="var(--success)"
                  strokeWidth="3"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6 }}
                />
                <motion.path
                  d="M15 27l7 7 15-16"
                  fill="none"
                  stroke="var(--success)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 0.45, duration: 0.4 }}
                />
              </svg>
              <p className="font-display text-xl font-bold text-text">{t('mfa.setup.success')}</p>
            </motion.div>
          ) : (
            <motion.div key="setup" exit={{ opacity: 0 }} className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-primary text-on-primary shadow-[0_10px_24px_-10px_var(--primary)]">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-text">{t('mfa.setup.title')}</h1>
                  <p className="mt-1 text-base text-muted">{reason}</p>
                </div>
              </div>

              {state === 'preparing' && !error ? (
                <Tide label={t('mfa.setup.preparing')} lines={[t('mfa.setup.preparing')]} />
              ) : (
                setup && (
                  <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
                    <motion.div
                      initial={{ opacity: 0, rotate: -4, scale: 0.9 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      className="mx-auto rounded-lg bg-white p-2 shadow-[0_18px_40px_-20px_var(--primary)]"
                    >
                      <img src={setup.qr} alt="" width={180} height={180} className="rounded-md" />
                    </motion.div>
                    <div className="flex flex-col gap-3">
                      <Step n={1} text={t('mfa.setup.step1')} />
                      <details className="group rounded-md border border-border bg-subtle/60 px-3 py-2 text-sm">
                        <summary className="cursor-pointer font-semibold text-text">
                          {t('mfa.setup.manualKey')}
                        </summary>
                        <button
                          className="mt-2 flex w-full items-center justify-between gap-2 rounded-sm bg-surface px-2 py-1.5 font-mono text-xs tracking-wider text-text"
                          onClick={() => {
                            void navigator.clipboard.writeText(setup.secret);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1600);
                          }}
                        >
                          <span className="break-all text-left">
                            {setup.secret.match(/.{1,4}/g)?.join(' ')}
                          </span>
                          {copied ? (
                            <Check size={14} className="text-success" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </details>
                      <Step n={2} text={t('mfa.setup.step2')} />
                    </div>
                  </div>
                )
              )}

              <OtpInput
                label={t('auth.totp.title')}
                value={code}
                onChange={setCode}
                onComplete={(v) => void verify(v)}
                error={Boolean(error)}
                disabled={state !== 'ready'}
              />
              {error && (
                <p role="alert" className="text-center text-sm font-semibold text-danger">
                  {error}
                </p>
              )}
              <Button
                size="lg"
                loading={state === 'verifying'}
                disabled={code.length < 6 || state !== 'ready'}
                onClick={() => void verify(code)}
              >
                {t('mfa.setup.verify')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex gap-3 text-sm text-text">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent text-xs font-bold text-on-accent">
        {n}
      </span>
      <p className="leading-relaxed">{text}</p>
    </div>
  );
}
